import { DateField } from "@overmap-ai/forms"
import type { ReactNode } from "react"
import type { DateRange } from "react-day-picker"

import type { SerializedDateRange } from "../../../typings"
import {
	DateAfterFilterModifier,
	DateBeforeFilterModifier,
	DateRangeInFilterModifier,
	DateRangeNotInFilterModifier,
	NullableDateEqualsFilterModifier,
	NullableDateNotEqualsFilterModifier,
} from "../../modifiers"
import { BaseFilterDef, createFilterModifierConfig, type FilterDefRenderProps } from "../BaseFilterDef"
import { DateFieldFilterCell } from "./DateFieldFilterCell"

type DateFieldValue = Date | null

type DateFieldFilterValue = Date | DateRange | null

type SerializedDateFieldFilterValue = string | SerializedDateRange | null

export class DateFieldFilterDef extends BaseFilterDef<
	DateFieldValue,
	DateFieldFilterValue,
	SerializedDateFieldFilterValue
> {
	readonly id: string
	readonly defaultFilterValue: DateFieldFilterValue | undefined = undefined
	readonly defaultFilterModifier: keyof typeof this.modifiers = "equals"
	readonly field: DateField

	constructor(field: DateField) {
		super()
		this.field = field
		this.id = field.id
	}

	readonly modifiers = {
		equals: createFilterModifierConfig({
			modifier: NullableDateEqualsFilterModifier,
			isValueValid: (value: DateFieldValue): value is Date | null => value !== null,
			isFilterValueValid: (filterValue: DateFieldFilterValue): filterValue is Date | null =>
				filterValue instanceof Date || filterValue === null,
		}),
		notEquals: createFilterModifierConfig({
			modifier: NullableDateNotEqualsFilterModifier,
			isValueValid: (value: DateFieldValue): value is Date | null => value !== null,
			isFilterValueValid: (filterValue: DateFieldFilterValue): filterValue is Date | null =>
				filterValue instanceof Date || filterValue === null,
		}),
		before: createFilterModifierConfig({
			modifier: DateBeforeFilterModifier,
			isValueValid: (value: DateFieldValue): value is Date => value !== null,
			isFilterValueValid: (filterValue: DateFieldFilterValue): filterValue is Date => filterValue instanceof Date,
		}),
		after: createFilterModifierConfig({
			modifier: DateAfterFilterModifier,
			isValueValid: (value: DateFieldValue): value is Date => value !== null,
			isFilterValueValid: (filterValue: DateFieldFilterValue): filterValue is Date => filterValue instanceof Date,
		}),
		in: createFilterModifierConfig({
			modifier: DateRangeInFilterModifier,
			isValueValid: (value: DateFieldValue): value is Date => value !== null,
			isFilterValueValid: (filterValue: DateFieldFilterValue): filterValue is DateRange =>
				"to" in (filterValue ?? {}) && "from" in (filterValue ?? {}),
		}),
		notIn: createFilterModifierConfig({
			modifier: DateRangeNotInFilterModifier,
			isValueValid: (value: DateFieldValue): value is Date => value !== null,
			isFilterValueValid: (filterValue: DateFieldFilterValue): filterValue is DateRange =>
				"to" in (filterValue ?? {}) && "from" in (filterValue ?? {}),
		}),
	}

	render(props: FilterDefRenderProps<DateFieldFilterDef>): ReactNode {
		return <DateFieldFilterCell key={props.filter.id} field={this.field} {...props} />
	}
}
